<template>
  <div>
    <div class="selection-grid">
      <div>
        <label class="field-label">University</label>
        <select
          v-if="allowedUniversities.length > 1"
          :value="selectedSlug"
          class="field-input"
          @change="onUniversityChange"
        >
          <option
            v-for="university in allowedUniversities"
            :key="university.slug"
            :value="university.slug"
          >
            {{ university.name }}
          </option>
        </select>
        <div v-else class="field-readonly">
          {{ allowedUniversities[0]?.name || selectedSlug || 'No university available' }}
        </div>
      </div>

      <div>
        <label class="field-label" for="ceremony-search-scope">Search within</label>
        <select
          id="ceremony-search-scope"
          :value="searchScope"
          class="field-input"
          @change="onSearchScopeChange"
        >
          <option value="ceremony">One ceremony</option>
          <option value="season">A whole season</option>
        </select>
      </div>

      <div v-if="searchScope === 'season'">
        <label class="field-label" for="ceremony-search-season">Season</label>
        <select
          id="ceremony-search-season"
          :value="selectedSeasonKey"
          class="field-input"
          :disabled="ceremoniesLoading || !ceremonySeasons.length"
          @change="onSeasonChange"
        >
          <option value="">Select season</option>
          <option
            v-for="season in ceremonySeasons"
            :key="season.key"
            :value="season.key"
          >
            {{ season.label }}
          </option>
        </select>
      </div>

      <div v-else>
        <div class="ceremony-label-row">
          <label class="field-label">Ceremony</label>
          <span
            v-if="selectedCeremonyId"
            class="readiness"
            :class="`readiness-${readinessTone}`"
          >
            <span class="readiness-dot"></span>
            {{ readinessLabel }}
          </span>
        </div>
        <select
          :value="selectedCeremonyId"
          class="field-input"
          :disabled="ceremoniesLoading || !ceremonies.length"
          @change="onCeremonyChange"
        >
          <option value="">
            {{ ceremoniesLoading ? 'Loading ceremonies…' : 'Select ceremony' }}
          </option>
          <option
            v-for="ceremony in ceremonies"
            :key="ceremony.id"
            :value="String(ceremony.id)"
          >
            {{ formatCeremony(ceremony) }}
          </option>
        </select>
      </div>
    </div>

    <form class="search-form" @submit.prevent="submitSearch">
      <label class="field-label" for="ceremony-intelligence-query">
        What would you like to find?
      </label>
      <div class="search-row">
        <input
          id="ceremony-intelligence-query"
          :value="queryText"
          type="search"
          class="field-input search-input"
          placeholder="Describe a moment, person or part of the ceremony"
          @input="$emit('update:queryText', $event.target.value)"
        />
        <button type="submit" class="submit-button" :disabled="!canSubmitSearch">
          <span v-if="canSubmitSearch">
            {{ searchScope === 'season' ? 'Search season' : 'Search ceremony' }}
          </span>
          <span v-else>Search</span>
        </button>
      </div>
      <div class="example-row" aria-label="Example searches">
        <span>Try</span>
        <button
          v-for="example in exampleQueries"
          :key="example"
          type="button"
          @click="$emit('update:queryText', example)"
        >
          {{ example }}
        </button>
      </div>
    </form>

    <p v-if="ceremonyError" class="form-error" role="alert">
      {{ ceremonyError }}
    </p>
  </div>
</template>

<script>
const READINESS = {
  idle: ['Select a ceremony', 'neutral'],
  loading: ['Checking readiness', 'neutral'],
  ready: ['Ready', 'ready'],
  processing: ['Preparing', 'working'],
  not_started: ['Not prepared', 'neutral'],
  no_vectors: ['Needs attention', 'warning'],
  failed: ['Preparation failed', 'warning'],
  unavailable: ['Unavailable', 'warning'],
}

export default {
  name: 'CeremonySearchControls',
  props: {
    allowedUniversities: {
      type: Array,
      default: () => [],
    },
    selectedSlug: {
      type: String,
      default: '',
    },
    ceremonies: {
      type: Array,
      default: () => [],
    },
    ceremoniesLoading: {
      type: Boolean,
      default: false,
    },
    ceremonyError: {
      type: String,
      default: '',
    },
    selectedCeremonyId: {
      type: String,
      default: '',
    },
    searchScope: {
      type: String,
      default: 'ceremony',
    },
    ceremonySeasons: {
      type: Array,
      default: () => [],
    },
    selectedSeasonKey: {
      type: String,
      default: '',
    },
    queryText: {
      type: String,
      default: '',
    },
    canSubmitSearch: {
      type: Boolean,
      default: false,
    },
    formatCeremony: {
      type: Function,
      required: true,
    },
    readiness: {
      type: String,
      default: 'idle',
    },
  },
  data() {
    return {
      exampleQueries: [
        'Welcome speech',
        'Audience cheering',
        'Honorary award',
        'Graduate celebration',
      ],
    }
  },
  computed: {
    readinessLabel() {
      return (READINESS[this.readiness] || READINESS.unavailable)[0]
    },
    readinessTone() {
      return (READINESS[this.readiness] || READINESS.unavailable)[1]
    },
  },
  methods: {
    onUniversityChange(event) {
      this.$emit('update:selectedSlug', event.target.value)
      this.$emit('university-change')
    },
    onCeremonyChange(event) {
      this.$emit('update:selectedCeremonyId', event.target.value)
      this.$emit('ceremony-change', event.target.value)
    },
    onSearchScopeChange(event) {
      this.$emit('update:searchScope', event.target.value)
      this.$emit('search-scope-change', event.target.value)
    },
    onSeasonChange(event) {
      this.$emit('update:selectedSeasonKey', event.target.value)
      this.$emit('season-change', event.target.value)
    },
    submitSearch() {
      if (this.canSubmitSearch) this.$emit('submit-search')
    },
  },
}
</script>

<style scoped>
.selection-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

@media (max-width: 820px) {
  .selection-grid {
    grid-template-columns: 1fr;
  }
}

.field-label {
  display: block;
  margin-bottom: 7px;
  color: #475467;
  font-size: 0.78rem;
  font-weight: 650;
}

.ceremony-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.field-input,
.field-readonly {
  width: 100%;
  min-height: 48px;
  border: 1px solid #d7dce5;
  border-radius: 10px;
  padding: 0 13px;
  outline: none;
  background: #fff;
  color: #172033;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.field-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.12);
}

.field-input:disabled,
.field-readonly {
  display: flex;
  align-items: center;
  background: #f8f9fb;
  color: #667085;
}

.readiness {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #667085;
  font-size: 0.7rem;
  font-weight: 700;
}

.readiness-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #98a2b3;
}

.readiness-ready {
  color: #067647;
}

.readiness-ready .readiness-dot {
  background: #12b76a;
}

.readiness-working {
  color: #6941c6;
}

.readiness-working .readiness-dot {
  background: #9e77ed;
  animation: readiness-pulse 1s ease-in-out infinite alternate;
}

.readiness-warning {
  color: #b54708;
}

.readiness-warning .readiness-dot {
  background: #f79009;
}

.search-form {
  margin-top: 20px;
}

.search-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
}

.search-input {
  appearance: none;
}

.submit-button {
  min-width: 156px;
  border: 0;
  border-radius: 10px;
  padding: 0 20px;
  background: #17132f;
  color: #fff;
  font-size: 0.84rem;
  font-weight: 750;
}

.submit-button:disabled {
  cursor: not-allowed;
  opacity: 0.42;
}

.example-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 10px;
  color: #7c8494;
  font-size: 0.72rem;
}

.example-row button {
  border: 1px solid #e4e0f2;
  border-radius: 999px;
  padding: 5px 10px;
  background: #faf9ff;
  color: #5f4b8b;
  font-weight: 650;
}

.example-row button:hover {
  border-color: #b9a8e8;
  background: #f4f0ff;
}

.form-error {
  margin: 12px 0 0;
  color: #b42318;
  font-size: 0.8rem;
}

@keyframes readiness-pulse {
  to {
    opacity: 0.35;
  }
}

@media (prefers-reduced-motion: reduce) {
  .field-input,
  .readiness-dot {
    animation: none;
    transition: none;
  }
}

@media (max-width: 767px) {
  .selection-grid,
  .search-row {
    grid-template-columns: 1fr;
  }

  .submit-button {
    min-height: 48px;
  }
}
</style>
